import React from "react";
import { motion } from "framer-motion";
import { Button } from "@/components/ui/button";
import { Phone, MessageCircle, ArrowRight, Mail, MapPin } from "lucide-react";
import { SCHOOL } from "@/lib/constants";

export const FinalCTA = () => {
  return (
    <section id="contact" data-testid="section-final-cta" className="py-16 sm:py-20 lg:py-24">
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-60px" }}
          transition={{ duration: 0.6, ease: [0.22, 1, 0.36, 1] }}
          className="relative overflow-hidden rounded-3xl bg-maple-navy text-maple-cream p-8 sm:p-12 lg:p-16 shadow-[0_30px_80px_rgba(31,45,36,0.25)]"
          data-testid="final-cta"
        >
          {/* Subtle corner wash */}
          <div
            className="absolute inset-0 pointer-events-none"
            style={{
              background:
                "radial-gradient(circle at 90% 10%, rgba(232,221,182,0.18), transparent 55%), radial-gradient(circle at 10% 90%, rgba(201,163,106,0.25), transparent 55%)",
            }}
            aria-hidden="true"
          />
          <div className="relative grid lg:grid-cols-12 gap-8 items-center">
            <div className="lg:col-span-7">
              <span className="kicker text-maple-tan" style={{ color: "#E8DDB6" }}>
                Ready to hit the road?
              </span>
              <h2 className="mt-3 font-serif text-3xl sm:text-4xl lg:text-5xl leading-[1.05] tracking-[-0.01em]">
                Book your first lesson today —
                <br className="hidden sm:block" />
                <span className="text-maple-tan">drive with confidence tomorrow.</span>
              </h2>
              <p className="mt-4 text-maple-cream/85 text-base sm:text-lg leading-relaxed max-w-xl">
                Tap call or WhatsApp — we'll match you with the right instructor and package for
                where you are today.
              </p>
            </div>

            <div className="lg:col-span-5 flex flex-col gap-3">
              <Button
                asChild
                size="lg"
                className="lift-on-hover h-14 px-6 bg-maple-cream text-maple-navy hover:bg-maple-cream2 hover:text-maple-navy text-base justify-between"
                data-testid="final-cta-call-button"
              >
                <a href={SCHOOL.phoneLink} aria-label={`Call ${SCHOOL.phoneDisplay}`}>
                  <span className="inline-flex items-center">
                    <Phone className="mr-2 h-5 w-5" aria-hidden="true" />
                    Call {SCHOOL.phoneDisplay}
                  </span>
                  <ArrowRight className="h-4 w-4" aria-hidden="true" />
                </a>
              </Button>
              <Button
                asChild
                size="lg"
                variant="outline"
                className="lift-on-hover h-14 px-6 bg-transparent border-maple-cream/40 text-maple-cream hover:bg-maple-cream hover:text-maple-navy text-base justify-between"
                data-testid="final-cta-whatsapp-button"
              >
                <a href={SCHOOL.whatsappLink} target="_blank" rel="noopener noreferrer">
                  <span className="inline-flex items-center">
                    <MessageCircle className="mr-2 h-5 w-5" aria-hidden="true" />
                    WhatsApp Us
                  </span>
                  <ArrowRight className="h-4 w-4" aria-hidden="true" />
                </a>
              </Button>
              <Button
                asChild
                size="lg"
                variant="outline"
                className="lift-on-hover h-14 px-6 bg-transparent border-maple-cream/40 text-maple-cream hover:bg-maple-cream hover:text-maple-navy text-base justify-between"
                data-testid="final-cta-email-button"
              >
                <a href={SCHOOL.emailLink} aria-label={`Email ${SCHOOL.email}`}>
                  <span className="inline-flex items-center">
                    <Mail className="mr-2 h-5 w-5" aria-hidden="true" />
                    {SCHOOL.email}
                  </span>
                  <ArrowRight className="h-4 w-4" aria-hidden="true" />
                </a>
              </Button>
              <a
                href={SCHOOL.mapsLink}
                target="_blank"
                rel="noopener noreferrer"
                className="mt-1 inline-flex items-center gap-2 text-sm text-maple-cream/80 hover:text-maple-tan transition-colors"
                data-testid="final-cta-maps-link"
              >
                <MapPin className="h-4 w-4" aria-hidden="true" />
                Find us on Google Maps
              </a>
              <p className="mt-2 text-xs text-maple-cream/70">
                Classes run 6 AM to 10 PM, 7 days a week
              </p>
            </div>
          </div>
        </motion.div>
      </div>
    </section>
  );
};
